import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import { apiClient } from '../../lib/api';
import { useAdminUsers, useAdminStores, useAdminGroups, useAdminRefetchAll } from '../../admin/context';

type AdminPermission = {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  accessLevel: 'store' | 'group';
  expiresAt: string | null;
  group: { id: string; slug: string; name: string } | null;
};

type AdminUser = {
  id: string;
  email: string | null;
  permissions: AdminPermission[];
};

type AdminStore = {
  id: string;
  slug: string;
  name: string;
  group: { id: string; slug: string; name: string } | null;
  [key: string]: any;
};

type AdminGroup = {
  id: string;
  name: string;
  slug: string;
};

type PermissionRow = {
  userId: string;
  userEmail: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  groupSlug: string | null;
  accessLevel: 'store' | 'group';
  expiresAt: string | null;
};

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const PermissionsManager: React.FC = () => {
  const { users, loading: usersLoading, error: usersError } = useAdminUsers();
  const { stores, loading: storesLoading, error: storesError } = useAdminStores();
  const { groups, loading: groupsLoading, error: groupsError } = useAdminGroups();
  const refetchAll = useAdminRefetchAll();
  const loading = usersLoading || storesLoading || groupsLoading;
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState<'all' | 'store' | 'group'>('all');

  // Show context errors
  useEffect(() => {
    const ctxError = usersError || storesError || groupsError;
    if (ctxError) setError(ctxError);
  }, [usersError, storesError, groupsError]);

  const permissionRows = useMemo<PermissionRow[]>(() => {
    return users.flatMap((user) =>
      (user.permissions || []).map((permission) => ({
        userId: user.id,
        userEmail: user.email ?? 'Unknown',
        tenantId: permission.tenantId,
        tenantSlug: permission.tenantSlug,
        tenantName: permission.tenantName,
        groupSlug: permission.group?.slug ?? null,
        accessLevel: permission.accessLevel as 'store' | 'group',
        expiresAt: permission.expiresAt,
      })),
    );
  }, [users]);

  const tenantOptions = useMemo(() => {
    const map = new Map<string, string>();
    permissionRows.forEach((row) => {
      if (!map.has(row.tenantSlug)) {
        map.set(row.tenantSlug, row.tenantName);
      }
    });
    return Array.from(map.entries()).map(([slug, name]) => ({ slug, name }));
  }, [permissionRows]);

  const filteredPermissions = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();
    return permissionRows.filter((row) => {
      const matchesSearch = !search || row.userEmail.toLowerCase().includes(search);
      const matchesTenant = tenantFilter === 'all' || row.tenantSlug === tenantFilter;
      const matchesAccess = accessFilter === 'all' || row.accessLevel === accessFilter;
      return matchesSearch && matchesTenant && matchesAccess;
    });
  }, [permissionRows, searchQuery, tenantFilter, accessFilter]);

  function getUserById(userId: string) {
    return users.find((user) => user.id === userId) || null;
  }

  async function handleGrantAccess(values: {
    userId: string;
    accessType: 'store' | 'group';
    targetSlug: string;
    expiresAt?: string | null;
  }) {
    setActionLoading('grant');
    setError(null);
    setNotice(null);
    try {
      const user = getUserById(values.userId);
      if (!user) {
        throw new Error('User not found');
      }
      const existingStoreSlugs = user.permissions
        .filter((permission) => permission.accessLevel === 'store' && permission.tenantSlug)
        .map((permission) => permission.tenantSlug);
      const existingGroupSlugs = user.permissions
        .filter((permission) => permission.accessLevel === 'group' && permission.group?.slug)
        .map((permission) => permission.group!.slug);

      if (values.accessType === 'store') {
        existingStoreSlugs.push(values.targetSlug);
      } else {
        existingGroupSlugs.push(values.targetSlug);
      }

      const payload: Record<string, any> = {
        stores: Array.from(new Set(existingStoreSlugs.filter(Boolean))),
        groups: Array.from(new Set(existingGroupSlugs.filter(Boolean))),
      };

      if (!payload.stores.length && !payload.groups.length) {
        throw new Error('At least one store or group must be selected');
      }

      if (values.expiresAt !== undefined) {
        payload.expiresAt = values.expiresAt;
      }

      const response = await apiClient.put(
        `/api/admin/users/${values.userId}/permissions`,
        payload,
      );
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to update permissions');
      }
      setShowGrantForm(false);
      setNotice('Permissions updated successfully');
      await refetchAll();
    } catch (err: any) {
      setError(err.message || 'Failed to grant access');
    }
    setActionLoading(null);
  }

  async function handleRevokePermission(row: PermissionRow) {
    setActionLoading(`revoke-${row.userId}-${row.tenantId}`);
    setError(null);
    setNotice(null);
    try {
      const payload =
        row.accessLevel === 'group'
          ? { groups: row.groupSlug ? [row.groupSlug] : [] }
          : { stores: row.tenantSlug ? [row.tenantSlug] : [] };
      const response = await apiClient.post(`/api/admin/users/${row.userId}/revoke`, payload);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to revoke access');
      }
      setNotice('Access revoked successfully');
      await refetchAll();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke access');
    }
    setActionLoading(null);
  }

  async function handleSetExpiration(userId: string, expiresAt: string | null) {
    const user = getUserById(userId);
    if (!user) {
      setError('User not found');
      return;
    }
    const storeSlugs = user.permissions
      .filter((permission) => permission.accessLevel === 'store' && permission.tenantSlug)
      .map((permission) => permission.tenantSlug);
    const groupSlugs = user.permissions
      .filter((permission) => permission.accessLevel === 'group' && permission.group?.slug)
      .map((permission) => permission.group!.slug);

    if (!storeSlugs.length && !groupSlugs.length) {
      setError('User has no permissions to update');
      return;
    }

    setActionLoading(`expire-${userId}`);
    setError(null);
    setNotice(null);
    try {
      const payload: Record<string, any> = {
        stores: storeSlugs,
        groups: groupSlugs,
        expiresAt: expiresAt,
      };
      const response = await apiClient.put(`/api/admin/users/${userId}/permissions`, payload);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to update expiration');
      }
      setNotice(expiresAt ? 'Expiration updated' : 'Expiration cleared');
      await refetchAll();
    } catch (err: any) {
      setError(err.message || 'Failed to update expiration');
    }
    setActionLoading(null);
  }

  function promptExpiration(row: PermissionRow) {
    const input = window.prompt(
      'Enter expiration date (YYYY-MM-DD). Leave blank to clear.',
      row.expiresAt ? row.expiresAt.slice(0, 10) : '',
    );
    if (input === null) return;
    const trimmed = input.trim();
    if (!trimmed) {
      handleSetExpiration(row.userId, null);
      return;
    }
    const parsed = new Date(`${trimmed}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) {
      setError('Invalid date format. Use YYYY-MM-DD.');
      return;
    }
    handleSetExpiration(row.userId, parsed.toISOString());
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Permissions
          </Typography>
          <Button variant="contained" onClick={() => setShowGrantForm(true)}>
            Grant Access
          </Button>
        </Box>

        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
          <TextField
            size="small"
            placeholder="Search by user email…"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Tenant</InputLabel>
            <Select
              value={tenantFilter}
              onChange={(event) => setTenantFilter(event.target.value)}
              label="Tenant"
            >
              <MenuItem value="all">All Tenants</MenuItem>
              {tenantOptions.map((tenant) => (
                <MenuItem key={tenant.slug} value={tenant.slug}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Access Level</InputLabel>
            <Select
              value={accessFilter}
              onChange={(event) => setAccessFilter(event.target.value as 'all' | 'store' | 'group')}
              label="Access Level"
            >
              <MenuItem value="all">All Access Levels</MenuItem>
              <MenuItem value="store">Store</MenuItem>
              <MenuItem value="group">Group</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {loading ? (
          <Typography sx={{ mt: 2 }}>Loading permissions...</Typography>
        ) : filteredPermissions.length ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Access Level</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPermissions.map((row) => (
                  <TableRow key={`${row.userId}-${row.tenantId}`}>
                    <TableCell>{row.userEmail}</TableCell>
                    <TableCell>
                      {row.tenantName}{' '}
                      {row.groupSlug ? (
                        <Typography variant="caption" color="text.secondary">
                          ({row.groupSlug})
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.accessLevel}
                        size="small"
                        color={row.accessLevel === 'group' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(row.expiresAt)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleRevokePermission(row)}
                          disabled={actionLoading === `revoke-${row.userId}-${row.tenantId}`}
                        >
                          {actionLoading === `revoke-${row.userId}-${row.tenantId}`
                            ? 'Revoking…'
                            : 'Revoke'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => promptExpiration(row)}
                          disabled={actionLoading === `expire-${row.userId}`}
                        >
                          {actionLoading === `expire-${row.userId}`
                            ? 'Updating…'
                            : 'Set Expiration'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{ mt: 2 }}>No permissions match the current filters.</Typography>
        )}
      </Paper>

      {showGrantForm && (
        <GrantAccessForm
          users={users}
          stores={stores}
          groups={groups}
          loading={actionLoading === 'grant'}
          onSubmit={handleGrantAccess}
          onCancel={() => setShowGrantForm(false)}
        />
      )}
    </Box>
  );
};

type GrantAccessFormProps = {
  users: AdminUser[];
  stores: AdminStore[];
  groups: AdminGroup[];
  loading: boolean;
  onSubmit: (values: {
    userId: string;
    accessType: 'store' | 'group';
    targetSlug: string;
    expiresAt?: string | null;
  }) => Promise<void> | void;
  onCancel: () => void;
};

const GrantAccessForm: React.FC<GrantAccessFormProps> = ({
  users,
  stores,
  groups,
  loading,
  onSubmit,
  onCancel,
}) => {
  const [userId, setUserId] = useState(users[0]?.id ?? '');
  const [accessType, setAccessType] = useState<'store' | 'group'>('store');
  const [targetSlug, setTargetSlug] = useState(stores[0]?.slug ?? '');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (accessType === 'store') {
      setTargetSlug((previous) => previous || (stores[0]?.slug ?? ''));
    } else {
      setTargetSlug((previous) => previous || (groups[0]?.slug ?? ''));
    }
  }, [accessType, stores, groups]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setLocalError(null);
    if (!userId) {
      setLocalError('Select a user');
      return;
    }
    if (!targetSlug) {
      setLocalError('Select a target');
      return;
    }
    let isoExpiration: string | null | undefined = undefined;
    if (expiresAt.trim()) {
      const parsed = new Date(`${expiresAt}T00:00:00Z`);
      if (Number.isNaN(parsed.getTime())) {
        setLocalError('Invalid expiration date');
        return;
      }
      isoExpiration = parsed.toISOString();
    }
    try {
      await onSubmit({
        userId,
        accessType,
        targetSlug,
        expiresAt: isoExpiration,
      });
    } catch (err: any) {
      setLocalError(err.message || 'Failed to grant access');
    }
  }

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Grant Access</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {localError && <Alert severity="error">{localError}</Alert>}
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
                label="User"
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email ?? user.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Access Type</InputLabel>
              <Select
                value={accessType}
                onChange={(event) => setAccessType(event.target.value as 'store' | 'group')}
                label="Access Type"
              >
                <MenuItem value="store">Store</MenuItem>
                <MenuItem value="group">Group</MenuItem>
              </Select>
            </FormControl>
            {accessType === 'store' ? (
              <FormControl fullWidth>
                <InputLabel>Store</InputLabel>
                <Select
                  value={targetSlug}
                  onChange={(event) => setTargetSlug(event.target.value)}
                  label="Store"
                >
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.slug}>
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Group</InputLabel>
                <Select
                  value={targetSlug}
                  onChange={(event) => setTargetSlug(event.target.value)}
                  label="Group"
                >
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.slug}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              type="date"
              label="Expiration (optional)"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving…' : 'Grant Access'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PermissionsManager;
